import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { auth } from "../../../firebase/config";

import PrimaryButton from "../../../components/Buttons/PrimaryButton";

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [verifying, setVerifying] = useState<boolean>(true);
  const [email, setEmail] = useState<string>("");

  const oobCode = searchParams.get("oobCode");

  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode) {
        setError("Invalid or missing reset code.");
        setVerifying(false);
        return;
      }

      try {
        const userEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(userEmail);
        setVerifying(false);
      } catch (error: any) {
        console.error("Code verification error:", error);

        if (error.code === "auth/invalid-action-code") {
          setError(
            "This password reset link is invalid or has already been used."
          );
        } else if (error.code === "auth/expired-action-code") {
          setError(
            "This password reset link has expired. Please request a new one."
          );
        } else {
          setError("Failed to verify reset link. Please try again.");
        }
        setVerifying(false);
      }
    };

    verifyCode();
  }, [oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!oobCode) {
      setError("Invalid reset code.");
      return;
    }

    setLoading(true);

    try {
      await confirmPasswordReset(auth, oobCode, password);

      navigate("/login", {
        state: {
          message:
            "Password reset successful! Please log in with your new password.",
        },
      });
    } catch (error: any) {
      console.error("Password reset error:", error);

      if (error.code === "auth/invalid-action-code") {
        setError(
          "This password reset link is invalid or has already been used."
        );
      } else if (error.code === "auth/expired-action-code") {
        setError(
          "This password reset link has expired. Please request a new one."
        );
      } else if (error.code === "auth/weak-password") {
        setError("Password is too weak. Please choose a stronger password.");
      } else {
        setError("Failed to reset password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="w-full max-w-md px-4">
          <div className="text-center">
            <h2 className="text-3xl font-semibold mb-4">Verifying...</h2>
            <p className="text-gray-600">
              Please wait while we verify your reset link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !email) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="w-full max-w-md px-4">
          <div className="text-left">
            <h2 className="text-3xl font-semibold mb-4">Invalid Reset Link</h2>
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
              role="alert"
            >
              {error}
            </div>
            <p className="text-gray-600 mb-6">
              Please request a new password reset link from the login page.
            </p>
            <PrimaryButton
              className="w-full"
              onClick={() => navigate("/forgotPassword")}
            >
              Request New Reset Link
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex justify-center items-center bg-gray-50">
      <div className="w-full max-w-md px-4">
        <div className="text-left">
          <h2 className="text-3xl font-semibold mb-4 text-left">
            Create new password
          </h2>
          <p className="text-gray-600 mb-2">
            Enter a new password for your account.
          </p>
          {email && (
            <p className="text-sm text-gray-500 mb-6">Account: {email}</p>
          )}

          <div className="max-w-md mx-auto">
            <form onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                  minLength={6}
                  placeholder="Enter new password"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must be at least 6 characters long
                </p>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">
                  Confirm Password
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  required
                  placeholder="Confirm new password"
                />
              </div>

              <PrimaryButton
                className="mt-6 w-full"
                type="submit"
                disabled={loading}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </PrimaryButton>

              {error && (
                <div
                  className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 mt-4"
                  role="alert"
                >
                  {error}
                </div>
              )}

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="text-[#1d365d] hover:underline"
                  disabled={loading}
                >
                  Back to login
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
